/**
 * The picker's pre-filter, running paio-auth's actual rule (ORDR-94).
 *
 * `GrantAdminService.create_grant` admits a non-operator when `access:grant` is
 * among their capabilities AT THE TARGET SCOPE and
 * `set(role.capabilities) <= actor_caps`. The second clause is a SUBSET test
 * over the role catalogue.
 *
 * WHY THIS IS NOT A COPY OF paio-ui's VERSION. `AccessConsole`'s
 * `grantableRoleKeys` offers only the role keys the actor LITERALLY HOLDS,
 * because `GET /access/grants` was all a client could read. That is strictly
 * narrower than the server rule, and the gap is the ordinary case: a
 * brand-admin is entitled to confer every other ordering role, each being a
 * subset of its capabilities, and was offered none of them. AUTH-978 added
 * `GET /access/catalog?app=ordering`, so this module runs the real rule.
 *
 * STILL ONLY A PRE-FILTER. The server decides every write and remains the only
 * authorization boundary; this file is a rendering decision, exactly as
 * CLAUDE.md requires. A wrong answer here costs a picker entry, never access.
 * What it buys is a form that never offers what the server would refuse, and
 * never hides what it would allow.
 *
 * A KNOWN NARROWING, AND IT IS NOT THE ONE ABOVE. paio-auth resolves
 * capabilities along the resource's ANCESTOR CHAIN (AUTH-528), so a brand-admin
 * genuinely holds `access:grant` at every location under the brand and the
 * server would allow `kitchen-display` there. This module offers only scopes
 * where the actor holds a DIRECT grant, because a client cannot see the scope
 * tree: `GET /access/grants` returns the actor's own grants, and the ordering
 * context's nodes carry `node_id`, `label`, `capabilities` and `site_id` with
 * no scope type and no parent. A descendant scope cannot be named here, and
 * inventing one would produce a 400 this pre-filter exists to prevent. So the
 * picker is narrower than the server in this one direction, on purpose, and
 * `grantPolicy.test.ts` pins it so the narrowing cannot be mistaken for a bug.
 *
 * FAILING CLOSED IS DELIBERATE. With no catalogue every helper returns nothing.
 * An unreadable catalogue must not degrade into "offer everything", nor quietly
 * back off to the held-roles behaviour this module exists to replace, because
 * both would be indistinguishable from working.
 */

import { GRANT_CAPABILITY } from "./capabilities.js";
import type { CatalogRole, Grant, Scope } from "./types.js";

/** One scope as a string: a React key, a `<select>` label, a map key. */
export function scopeKey(scope: Scope): string {
  return `${scope.scopeType}:${scope.scopeId}`;
}

function sameScope(grant: Grant, scope: Scope): boolean {
  return grant.scope_type === scope.scopeType && grant.scope_id === scope.scopeId;
}

/**
 * The capabilities the actor holds at one scope: the union over every role
 * they are granted there.
 *
 * A held role the catalogue does not describe contributes nothing. `Grant.role_key`
 * is a CharField with no foreign key, so a role pruned from the manifest can
 * outlive its grant; it confers nothing nameable and must widen nothing.
 */
export function capabilitiesAt(
  grants: readonly Grant[],
  ownUserId: string,
  catalog: readonly CatalogRole[],
  scope: Scope,
): Set<string> {
  const byKey = new Map(catalog.map((role) => [role.key, role]));
  const held = new Set<string>();
  for (const grant of grants) {
    if (grant.user_id !== ownUserId || !sameScope(grant, scope)) {
      continue;
    }
    for (const capability of byKey.get(grant.role_key)?.capabilities ?? []) {
      held.add(capability);
    }
  }
  return held;
}

/**
 * The roles the actor may confer at `scope`, sorted by key.
 *
 * Three conditions, and all three are the server's:
 *   1. the actor holds `access:grant` there, so they may delegate at all;
 *   2. the role is granted AT this scope type, or the write is refused for the
 *      mismatch before authority is even considered;
 *   3. the role's capabilities are a SUBSET of the actor's there.
 */
export function grantableRoles(
  grants: readonly Grant[],
  ownUserId: string,
  catalog: readonly CatalogRole[],
  scope: Scope,
): CatalogRole[] {
  const mine = capabilitiesAt(grants, ownUserId, catalog, scope);
  if (!mine.has(GRANT_CAPABILITY)) {
    return [];
  }
  return catalog
    .filter(
      (role) =>
        role.scope_type === scope.scopeType &&
        role.capabilities.every((capability) => mine.has(capability)),
    )
    .sort((left, right) => left.key.localeCompare(right.key));
}

/**
 * The scopes the actor administers: those where they hold `access:grant`.
 *
 * Deduped, and in the order the grants arrived, so a scope reached through two
 * roles appears once and the list does not reshuffle between reads.
 */
export function manageableScopes(
  grants: readonly Grant[],
  ownUserId: string,
  catalog: readonly CatalogRole[],
): Scope[] {
  const seen = new Map<string, Scope>();
  for (const grant of grants) {
    if (grant.user_id !== ownUserId) {
      continue;
    }
    const scope: Scope = { scopeType: grant.scope_type, scopeId: grant.scope_id };
    const key = `${scope.scopeType} ${scope.scopeId}`;
    if (seen.has(key)) {
      continue;
    }
    if (capabilitiesAt(grants, ownUserId, catalog, scope).has(GRANT_CAPABILITY)) {
      seen.set(key, scope);
    }
  }
  return [...seen.values()];
}
